import { platformService } from '../../../shared/services/platform.service';
import { STATUS_GUID } from '../../../shared/utils/constants';
import { ticketSearchFilter } from '../../../shared/utils/ticketSearchFilter';
import { useContext } from 'react';
import { AppStateContext } from '../../../AppState';
import { RequestListContext } from './RequestContextProvider';
import { getCachedUsers } from '../../../shared/cache/PlatformUserCache';

const api = new platformService();

const RequestFunctions = () => {
  const state = useContext(AppStateContext);
  const { requestStates, setRequestStates, approvalStates, setApprovalStates, showClosed, searchFilter } = useContext(RequestListContext);

  const moveToRequestPage = (action: string) => {
    setRequestStates((prev) => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false }));

    if (action === 'next') {
      setRequestStates((prev) => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false, currentPage: prev.currentPage + 1 }));
      getPaginatedRequests(requestStates.currentPage + 1);
    } else if (action === 'previous') {
      setRequestStates((prev) => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false, currentPage: prev.currentPage - 1 }));
      getPaginatedRequests(requestStates.currentPage - 1);
    } else {
      const thisPage = parseInt(action) || 1
      setRequestStates((prev) => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false, currentPage:thisPage }));
      getPaginatedRequests(thisPage);
    }
  };
  const countPerRequestPage = (count: number) => {
    localStorage.setItem('requestListItemPerPage', count.toString());
    setRequestStates(prev => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false, countPerPage: count }));
    getPaginatedRequests(requestStates.currentPage, count);
  }

  const moveToApprovalPage = (action: string) => {
    setApprovalStates((prev) => ({ ...prev, isApprovalsFetching: true }));

    if (action === 'next') {
      setApprovalStates((prev) => ({ ...prev, isApprovalsFetching: true, currentPage: prev.currentPage + 1 }));
      getPaginatedApprovals(approvalStates.currentPage + 1);
    } else if ( action === 'previous') {
      setApprovalStates((prev) => ({ ...prev, isApprovalsFetching: true, currentPage: prev.currentPage - 1 }));
      getPaginatedApprovals(approvalStates.currentPage - 1);
    } else {
      const thisPage = parseInt(action) || 1
      setApprovalStates((prev) => ({ ...prev, isApprovalsFetching: true, currentPage:thisPage }));
      getPaginatedApprovals(thisPage);
    }
  };
  const countPerApprovalPage = (count: number) => {
    localStorage.setItem('approvalListItemPerPage', count.toString());
    setApprovalStates(prev => ({ ...prev, isApprovalsFetching: true, countPerPage: count }));
    getPaginatedApprovals(approvalStates.currentPage, count);
  }

  const getPaginatedRequests = async (page: number, count?: number, closed?: boolean, search?: string) => {
    count ??= requestStates.countPerPage;
    closed ??= showClosed;
    search ??= searchFilter;
    try {
      const expandQuery = "AffectedUsers($select=PlatformUserId),TicketCollaborators($select=PlatformUserId),FileAttachments($filter=IsPublic eq true;$top=1;$orderby=Id desc;),Comments($filter=IsPublic eq true and IsDeleted eq false;$top=1;$orderby=Id desc;$select=Id,Body,CreatedById,IsPublic,IsDeleted),TicketLifecycle($expand=Status,Lifecycle($select=Title),Phases($filter=IsCurrent eq true))";
      let filterQuery = `Closed eq ${closed} and Status/Guid ne ${STATUS_GUID.DEFLECTED} and (RequesterId eq ${state.currentUserId} or AffectedUsers/any(a: a/PlatformUserId eq ${state.currentUserId}))`
      if (search)
        filterQuery += `and ${ticketSearchFilter(search, { matchId: true, comments: 'public' })}`;
        
      const data = (await api.getTicketList(`?$expand=${expandQuery}&$filter=${filterQuery}&$orderby=ModifiedDate desc&$top=${count}&$count=true&$skip=${(page - 1) * count}`))?.data;
      const users = await getCachedUsers(data.value.map(x => [x.RequesterId, x.AssigneeId, ...x.AffectedUsers.map(a => a.PlatformUserId), ...x.TicketCollaborators.map(a => a.PlatformUserId), ...x.Comments.map(a => a.CreatedById)]).flat().filter(y => y));
      data.value.forEach(t => {
        t.Requester = users.find(u => u.Id == t.RequesterId);
        t.Assignee = users.find(u => u.Id == t.AssigneeId);
        t.AffectedUsers?.forEach(a => a.PlatformUser = users.find(u => u.Id == a.PlatformUserId));
        t.TicketCollaborators?.forEach(a => a.PlatformUser = users.find(u => u.Id == a.PlatformUserId));
        t.Comments?.forEach(a => a.CreatedBy = users.find(u => u.Id == a.CreatedById));
      });

      setRequestStates(prev => ({ 
        ...prev,
        isTicketsFetching: false,
        countTickets: data['@odata.count'],
        tickets: data.value
      }));
    } catch (error) {
      console.error('Error occurred fetching requests', error);
    }
  };

  const getPaginatedApprovals = async (page: number, count?: number, closed?: boolean, search?: string) => {
    count ??= approvalStates.countPerPage;
    closed ??= showClosed;
    search ??= searchFilter ?? '';
    try {
      const data = await api.getApproversByUser(state.currentUserId, `?showClosed=${closed}&search=${search}&$skip=${(page - 1) * count}&$top=${count}`);

      var approvals = JSON.parse(data.approvals).map((a: any) => ({
        ...a, 
        Ticket: { 
          Id: a.TicketId, 
          Title: a.TicketTitle, 
          StatusId: a.StatusId, 
          Closed: a.Closed, 
          TicketLifecycle: (a.LifecycleName ? { Lifecycle: { Title: a.LifecycleName }, Status: { Guid: a.LifecycleStatus }, Phases: [{ Name: a.ActivePhaseName }] } : null), 
          Status: state.ticketStatus.find((status: any) => status.Id === a.StatusId) 
        }
      }));

      setApprovalStates(prev => ({
        ...prev,
        isApprovalsFetching: false,
        countApprovals: data.count,
        approvals: approvals,
        isInitialLoad: false
      }));
    } catch (error) {
      console.error('Error occurred fetching approvals', error);
    }
  };

  return {
    getPaginatedRequests,
    moveToRequestPage,
    countPerRequestPage,
    getPaginatedApprovals,
    moveToApprovalPage,
    countPerApprovalPage
  };
};

export default RequestFunctions;